'use client'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Icon,
} from '@/dc-shared-web/src'
import { faXmark } from '@document-crunch/pro-regular-svg-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { useAPIToken } from '@dc-shared-web/providers/APITokenProvider'
import ProjectSightLogo from '@/integrations/projectsight/ProjectSightLogo'

// Import custom hooks
import useTrimbleData from '@/app/core/components/ProjectSight/hooks/useTrimbleData'
import useProjectSightExport from '@/app/core/components/ProjectSight/hooks/useProjectSightExport'

// Import components
import LoadingState from '@/app/core/components/ProjectSight/LoadingState'
import SuccessState from '@/app/core/components/ProjectSight/SuccessState'
import ErrorState from '@/app/core/components/ProjectSight/ErrorState'
import FormState from '@/app/core/components/ProjectSight/FormState'

// Define the form schema with validation
const formSchema = z.object({
  portfolio: z.string().min(1, { message: 'Portfolio is required' }),
  project: z.string().min(1, { message: 'Project is required' }),
  contract: z.string().min(1, { message: 'Contract is required' }),
  selectedChecklists: z
    .array(
      z.object({
        id: z.string(),
        include: z.boolean().optional(),
      })
    )
    .optional(),
  selectedPlaybooks: z
    .array(
      z.object({
        id: z.string(),
        include: z.boolean().optional(),
      })
    )
    .optional(),
  documentOptions: z.record(z.string(), z.boolean()).optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ChecklistOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  optionGroup: 1 | 2 | 3
  questionOptions: {
    label: string
    fieldName: string
  }[]
  additionalOptions: {
    label: string
    fieldName: string
    show?: boolean
  }[]
}

interface PlaybookOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  additionalOptions: never[]
}

interface ProjectSightModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  documentName?: string
  crunchUUID?: string
  playbookOptions?: PlaybookOption[]
  checklistOptions?: ChecklistOption[]
}

/**
 * ProjectSight Modal Component
 * Allows users to export checklists and playbooks to ProjectSight
 */
export default function ProjectSightModal({
  open,
  setOpen,
  documentName = '',
  crunchUUID,
  checklistOptions,
  playbookOptions,
}: ProjectSightModalProps) {
  const { token: dcrunchToken } = useAPIToken()

  // Use custom hooks for data fetching and export functionality
  const trimbleData = useTrimbleData({
    isOpen: open,
    crunchUUID,
    documentName,
  })

  const projectSightExport = useProjectSightExport({
    crunchUUID,
    documentName,
    dcrunchToken,
  })

  // Initialize form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      portfolio: '',
      project: '',
      contract: '',
      selectedChecklists:
        checklistOptions?.map((option) => ({
          id: option.id,
          include: true,
        })) || [],
      selectedPlaybooks:
        playbookOptions?.map((option) => ({
          id: option.id,
          include: true,
        })) || [],
    },
  })

  // Watch form values to enable/disable fields and export button
  const watchPortfolio = form.watch('portfolio')
  const watchProject = form.watch('project')
  const watchContract = form.watch('contract')
  const watchSelectedChecklists = form.watch('selectedChecklists')
  const watchSelectedPlaybooks = form.watch('selectedPlaybooks')

  // Update trimbleData when form values change
  if (watchPortfolio !== trimbleData.selectedPortfolio) {
    trimbleData.setSelectedPortfolio(watchPortfolio)
  }

  if (watchProject !== trimbleData.selectedProject) {
    trimbleData.setSelectedProject(watchProject)
  }

  if (watchContract !== trimbleData.selectedContract) {
    trimbleData.setSelectedContract(watchContract)
  }

  // Determine if the export button should be enabled
  const isExportDisabled =
    !watchPortfolio ||
    !watchProject ||
    !watchContract ||
    // Check if at least one checklist or playbook is selected
    ((!watchSelectedChecklists ||
      !watchSelectedChecklists.some((c) => c?.include)) &&
      (!watchSelectedPlaybooks ||
        !watchSelectedPlaybooks.some((p) => p?.include)))

  // Handle form submission
  const handleSubmit = (values: FormValues) => {
    projectSightExport.submitExport(values)
  }

  // Handle modal close
  const handleClose = () => {
    setOpen(false)
    form.reset()
    projectSightExport.resetModalState()
    trimbleData.reset()
  }

  // Render different content based on modal state
  const renderModalContent = () => {
    switch (projectSightExport.modalState) {
      case 'loading':
        return <LoadingState />
      case 'success':
        return (
          <SuccessState documentName={documentName} onClose={handleClose} />
        )
      case 'error':
        return (
          <ErrorState
            documentName={documentName}
            errorMessage={projectSightExport.modalStateData.errorMessage}
            onBack={projectSightExport.resetModalState}
          />
        )
      default:
        return (
          <FormState
            form={form}
            documentName={documentName}
            checklistOptions={checklistOptions}
            playbookOptions={playbookOptions}
            portfolios={trimbleData.portfolios}
            projects={trimbleData.projects}
            contracts={trimbleData.contracts}
            loadingPortfolios={trimbleData.loadingPortfolios}
            loadingProjects={trimbleData.loadingProjects}
            loadingContracts={trimbleData.loadingContracts}
            watchPortfolio={watchPortfolio}
            watchProject={watchProject}
            isExportDisabled={isExportDisabled}
            onCancel={handleClose}
            onSubmit={handleSubmit}
          />
        )
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
      data-testid='projectsight-export-modal'
    >
      <DialogContent
        className='tw-w-[480px] tw-h-auto tw-p-5'
        aria-labelledby='projectsight-dialog-title'
        aria-describedby='projectsight-dialog-description'
      >
        <DialogHeader className='tw-flex tw-flex-row tw-items-center tw-justify-between'>
          <div className='tw-flex tw-items-center'>
            <ProjectSightLogo className='tw-h-10 tw-w-10' aria-hidden='true' />
            <DialogTitle
              className='tw-text-[24px] tw-font-semibold tw-text-[#2D3648] tw-text-center'
              style={{ fontFamily: 'var(--font-poppins)', lineHeight: '34px' }}
              id='projectsight-dialog-title'
            >
              Export to ProjectSight
            </DialogTitle>
          </div>
          <Button
            variant='ghost'
            size='icon'
            onClick={handleClose}
            className='tw-h-8 tw-w-8'
            aria-label='Close dialog'
          >
            <Icon icon={faXmark} size='2xl' className='tw-text-neutral-700' />
          </Button>
        </DialogHeader>

        {renderModalContent()}
      </DialogContent>
    </Dialog>
  )
}
